import {get, post, patch, del} from './http';

export type Folder = {
  id: string;
  name: string;
  created_by: string;
  created_at: string;
  job_count: number;
  /**
   Null for a top-level folder. Also null when the parent exists but was not shared with this
   analyst - the API hides it rather than naming a folder nobody shared, so the folder renders at
   the top level instead of hanging off a parent that never arrived.
   */
  parent_folder_id: string | null;
  /** Job_count for this folder and every folder beneath it - what the aggregate view covers. */
  total_job_count: number;
  /**
   Jobs created by the caller, in this folder and everything beneath it - authoritative over the
   whole subtree regardless of how many pages of jobs the client has loaded, unlike a count taken
   from the paginated job list.
   */
  total_mine_job_count: number;
};

export type UpdateFolderRequest = {
  name?: string;
  parent_folder_id?: string;
  /** Promotes the folder back to the top level. Ignored when parent_folder_id is also set. */
  clear_parent?: boolean;
};

export const foldersApi = {
  /** `createdBy` narrows to those creators' folders (and the ones above them). */
  list: async (createdBy: string[] = []) =>
    get<Folder[]>('/folders', createdBy.length > 0 ? {created_by: createdBy} : undefined),
  create: async (name: string, parentFolderId?: string) =>
    post<{name: string; parent_folder_id?: string}, Folder>('/folders', {
      name,
      ...(parentFolderId ? {parent_folder_id: parentFolderId} : {})
    }),
  update: async (id: string, body: UpdateFolderRequest) =>
    patch<UpdateFolderRequest, Folder>(`/folders/${id}`, body),
  delete: async (id: string) => del(`/folders/${id}`)
};

export type FolderRow = {folder: Folder; depth: number};

export type FolderRailItem = {kind: 'header' | 'body'; folder: Folder; depth: number};

/**
 Turns flattenFolders' rows into the sidebar's render list: one header per folder, plus a body (the
 folder's own investigations) for each expanded folder, emitted after the folder's whole subtree
 so subfolders stay pinned above the investigations. `rows` is a depth-first walk, so a body is
 due just before the next row at the same or a shallower depth, or at the end.
 */
export function withFolderBodies(rows: FolderRow[], expanded: Set<string>): FolderRailItem[] {
  const railItems: FolderRailItem[] = [];
  const open: FolderRow[] = [];
  const flushTo = (depth: number) => {
    while (open.length > 0 && (open.at(-1)?.depth ?? -1) >= depth) {
      const {folder, depth: openDepth} = open.pop()!;
      railItems.push({kind: 'body', folder, depth: openDepth});
    }
  };

  for (const {folder, depth} of rows) {
    flushTo(depth);
    railItems.push({kind: 'header', folder, depth});
    if (expanded.has(folder.id)) {
      open.push({folder, depth});
    }
  }

  flushTo(0);
  return railItems;
}

/**
 Folders in tree order, each carrying its depth for indentation. Flattening rather than nesting
 components keeps the sidebar's render one flat list, the way ClassificationVisibilityAdmin
 handles the taxonomy tree.

 Pass `expanded` to hide collapsed folders' subtrees; omit it (the folder <select>) to get
 everything.
 */
export function flattenFolders(folders: Folder[], expanded?: Set<string>): FolderRow[] {
  const known = new Set(folders.map((f) => f.id));
  const childrenByParent = new Map<string | null, Folder[]>();
  for (const folder of folders) {
    // Belt and braces with the API's own nulling: a parent id pointing at a folder that is not in
    // this list would otherwise strand the child in a walk that starts from the roots.
    const key =
      folder.parent_folder_id && known.has(folder.parent_folder_id)
        ? folder.parent_folder_id
        : null;
    childrenByParent.set(key, [...(childrenByParent.get(key) ?? []), folder]);
  }

  // Which folders a walk from the roots can reach at all, ignoring collapse. Only a parent cycle
  // - which the API refuses to create - can leave one out, and the sweep below catches those.
  const reachable = new Set<string>();
  const queue = (childrenByParent.get(null) ?? []).map((f) => f.id);
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (!reachable.has(id)) {
      reachable.add(id);
      queue.push(...(childrenByParent.get(id) ?? []).map((f) => f.id));
    }
  }

  const rows: FolderRow[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const folder of childrenByParent.get(parentId) ?? []) {
      rows.push({folder, depth});
      if (!expanded || expanded.has(folder.id)) {
        walk(folder.id, depth + 1);
      }
    }
  };

  walk(null, 0);

  // A folder no root can reach is still shown, at the top level - rendering one in the wrong
  // place is recoverable, one silently missing from the sidebar is not. Collapsed folders are
  // untouched by this: they are reachable, just deliberately not drawn.
  for (const folder of folders) {
    if (!reachable.has(folder.id)) {
      rows.push({folder, depth: 0});
    }
  }

  return rows;
}

/** A folder plus every folder beneath it - the set a folder aggregate covers. */
export function folderSubtreeIds(folders: Folder[], folderId: string): Set<string> {
  const childrenByParent = new Map<string | null, Folder[]>();
  for (const folder of folders) {
    const key = folder.parent_folder_id;
    childrenByParent.set(key, [...(childrenByParent.get(key) ?? []), folder]);
  }

  const ids = new Set([folderId]);
  const queue = [folderId];
  while (queue.length > 0) {
    for (const child of childrenByParent.get(queue.shift() ?? null) ?? []) {
      // The set doubles as the cycle guard, so bad data cannot hang the render.
      if (!ids.has(child.id)) {
        ids.add(child.id);
        queue.push(child.id);
      }
    }
  }

  return ids;
}
